import { useState } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "@shared/components/Button";
import { ConfirmDialog } from "@shared/components/ConfirmDialog";
import { StatusBadge } from "@shared/components/StatusBadge";
import { useAsync } from "@shared/hooks/useAsync";
import { useUnsavedGuard } from "@shared/hooks/useUnsavedGuard";
import { ApiClientError } from "@shared/services/api-client";
import {
  changeStatus,
  createArticle,
  deleteArticle,
  getArticle,
  listAssessments,
  updateArticle,
} from "./articles.api";
import { ArticleForm } from "./ArticleForm";
import { emptyForm, isDirty, toForm, toInput, validateForm, type ArticleFormValues, type FormErrors } from "./formModel";
import { BodyPreview } from "./BodyPreview";
import { FeatureDate } from "./FeatureDate";
import type { ArticleDetail, StatusAction } from "./types";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type Pending = StatusAction | "delete";

const CONFIRM: Record<Pending, { title: string; text: string; label: string; destructive?: boolean; done: string }> = {
  publish: { title: "Terbitkan artikel?", text: "Artikel akan tampil ke semua pengguna aplikasi.", label: "Ya, terbitkan", done: "Artikel diterbitkan" },
  unpublish: { title: "Tarik artikel?", text: "Artikel hilang dari aplikasi dan kembali menjadi draf.", label: "Ya, tarik", done: "Artikel ditarik ke draf" },
  archive: { title: "Arsipkan artikel?", text: "Artikel hilang dari aplikasi. Anda bisa memulihkannya sebagai draf.", label: "Ya, arsipkan", done: "Artikel diarsipkan" },
  restore: { title: "", text: "", label: "", done: "Artikel dipulihkan ke draf" },
  delete: { title: "Hapus draf?", text: "Draf dihapus permanen dan tidak bisa dibatalkan.", label: "Ya, hapus", destructive: true, done: "Draf dihapus" },
};

const FIELDS = ["title", "summary", "kind", "tags", "relatedAssessmentCode", "coverImageUrl", "bodySource", "isPlaceholder"] as const;

/** Joi reports { field, message } pairs; put each next to its input. */
function fieldErrorsFrom(err: unknown): FormErrors {
  const out: FormErrors = {};
  if (!(err instanceof ApiClientError) || !Array.isArray(err.details)) return out;
  for (const d of err.details as Array<{ field?: unknown; message?: unknown }>) {
    const name = String(d.field ?? "").split(".")[0] as keyof ArticleFormValues;
    if ((FIELDS as readonly string[]).includes(name) && typeof d.message === "string") out[name] ??= d.message;
  }
  return out;
}

function EditorBody({ initial }: { initial: ArticleDetail | null }) {
  const navigate = useNavigate();
  const [saved, setSaved] = useState<ArticleDetail | null>(initial);
  const [baseline, setBaseline] = useState<ArticleFormValues>(initial ? toForm(initial) : emptyForm);
  const [form, setForm] = useState<ArticleFormValues>(baseline);
  const [errors, setErrors] = useState<FormErrors>({});
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);

  const dirty = isDirty(form, baseline);
  const { blocker, allowLeave } = useUnsavedGuard(dirty);
  const assessments = useAsync(listAssessments, []);

  function fail(err: unknown) {
    const fields = fieldErrorsFrom(err);
    if (Object.keys(fields).length > 0) {
      setErrors(fields);
      setNotice("Periksa kembali isian yang bertanda.");
    } else {
      setNotice(err instanceof Error ? err.message : "Terjadi kesalahan");
    }
  }

  /** Validates and stores the form. Returns the stored article, or null when nothing was saved. */
  async function persist(): Promise<ArticleDetail | null> {
    const found = validateForm(form);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setNotice("Periksa kembali isian yang bertanda.");
      return null;
    }
    try {
      const input = toInput(form);
      const stored = saved ? await updateArticle(saved.id, input) : await createArticle(input);
      setBaseline(form);
      setSaved(stored);
      setNotice("");
      toast.success(stored.status === "draft" ? "Draf disimpan" : "Perubahan disimpan");
      if (!saved) {
        allowLeave.current = true;
        navigate(`/articles/${stored.id}`, { replace: true });
      }
      return stored;
    } catch (err) {
      fail(err);
      return null;
    }
  }

  async function run(work: () => Promise<void>) {
    if (busy) return;
    setBusy(true);
    setNotice("");
    try {
      await work();
    } finally {
      setBusy(false);
    }
  }

  const save = () => run(async () => void (await persist()));

  function perform(action: Pending) {
    setPending(null);
    return run(async () => {
      try {
        if (action === "delete") {
          await deleteArticle(saved!.id);
          allowLeave.current = true;
          toast.success(CONFIRM.delete.done);
          navigate("/articles");
          return;
        }
        // Publishing sends what the server has, so unsaved edits are stored first.
        if (action === "publish" && dirty && !(await persist())) return;
        setSaved(await changeStatus(saved!.id, action));
        toast.success(CONFIRM[action].done);
      } catch (err) {
        fail(err);
      }
    });
  }

  const status = saved?.status;
  const confirmCopy = pending ? CONFIRM[pending] : null;

  return (
    <section className="editor">
      <Link to="/articles">← Kembali ke daftar</Link>
      <header className="page-head">
        <h2>{saved ? "Sunting Artikel" : "Artikel Baru"}</h2>
        {saved && (
          <span data-testid="article-status">
            <StatusBadge status={saved.status} />
          </span>
        )}
      </header>

      {saved?.isPlaceholder && (
        <p className="muted">Konten placeholder. Menyimpan tanpa mencentang kotak placeholder menandainya sebagai konten asli.</p>
      )}
      {notice && (
        <p role="alert" className="form-error">
          {notice}
        </p>
      )}

      <div className="editor-grid">
        <div>
          <ArticleForm
            values={form}
            errors={errors}
            assessments={assessments.data ?? []}
            onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
          />
          <div className="row actions">
            <Button disabled={busy || (!!saved && !dirty)} onClick={() => void save()}>
              {busy ? "Menyimpan…" : status === undefined || status === "draft" ? "Simpan Draf" : "Simpan Perubahan"}
            </Button>
            {status === "draft" && (
              <Button variant="secondary" disabled={busy} onClick={() => setPending("publish")}>
                Terbitkan
              </Button>
            )}
            {status === "published" && (
              <>
                <Button variant="secondary" disabled={busy} onClick={() => setPending("unpublish")}>
                  Tarik
                </Button>
                <Button variant="secondary" disabled={busy} onClick={() => setPending("archive")}>
                  Arsipkan
                </Button>
              </>
            )}
            {status === "archived" && (
              <Button variant="secondary" disabled={busy} onClick={() => void perform("restore")}>
                Pulihkan
              </Button>
            )}
            {saved && status === "draft" && saved.publishedAt === null && (
              <Button variant="danger" disabled={busy} onClick={() => setPending("delete")}>
                Hapus
              </Button>
            )}
          </div>
          {saved && saved.kind === "tip" && saved.status === "published" && (
            <FeatureDate article={saved} onChange={setSaved} />
          )}
        </div>
        <BodyPreview source={form.bodySource} />
      </div>

      {confirmCopy && pending && (
        <ConfirmDialog
          title={confirmCopy.title}
          confirmLabel={confirmCopy.label}
          destructive={confirmCopy.destructive}
          onConfirm={() => void perform(pending)}
          onCancel={() => setPending(null)}
        >
          {confirmCopy.text}
        </ConfirmDialog>
      )}
      {blocker.state === "blocked" && (
        <ConfirmDialog
          title="Tinggalkan halaman?"
          confirmLabel="Tinggalkan"
          cancelLabel="Tetap di sini"
          destructive
          onConfirm={() => blocker.proceed()}
          onCancel={() => blocker.reset()}
        >
          Perubahan belum disimpan dan akan hilang.
        </ConfirmDialog>
      )}
    </section>
  );
}

function ExistingArticle({ id }: { id: string }) {
  const notFound = !UUID.test(id);
  const { data, error, errorStatus, loading, reload } = useAsync(
    () => (notFound ? Promise.resolve(null) : getArticle(id)),
    [id],
  );

  if (notFound || errorStatus === 404) {
    return (
      <section>
        <p>Artikel tidak ditemukan.</p>
        <Link to="/articles">← Kembali ke daftar</Link>
      </section>
    );
  }
  if (error) {
    return (
      <section role="alert" className="form-error">
        <p>{error}</p>
        <Button variant="secondary" onClick={reload}>
          Coba lagi
        </Button>
      </section>
    );
  }
  if (loading || !data) return <p role="status">Memuat…</p>;
  return <EditorBody initial={data} />;
}

export function ArticleEditorPage() {
  const { id } = useParams();
  return id ? <ExistingArticle key={id} id={id} /> : <EditorBody initial={null} />;
}
